/* eslint-disable linebreak-style */
import { CustomMessage, Message, MessageStatus } from '@azure/communication-react';
import { useState, useCallback, useEffect } from 'react';
import _, { compact } from 'underscore';
import { ChatCompletionRequestMessageRoleEnum } from 'openai';
import { v4 as uuid } from 'uuid';
import { useMsal, useAccount } from '@azure/msal-react';
import { useLLMSetting } from '../Contexts';
import { ChatRole, useTextCompletion, ChatMessage as ChatMessageDto } from '../../Repository/TextCompletion';
import { useBingSerp } from '../../Repository/BingSerp';
import { useLogger } from '../Logger';
import { LLMModel, PromptingMethod } from '../Contexts/LLMSettingContext';
import { ChatMessage, IMultiDeploymentChatHistory, IMultiDeploymentChatHistoryItem } from '../CustomMessage/message-types';
import { MARKDOWN_PREFIX } from '../DailyPlanner/common';
import { useIsVivara } from '../../hooks/useIsVivara';
import { Topic } from '../DeveloperPanel/userModeSetting';

export const botName = process.env.REACT_APP_BOT_NAME;

let messageId = 0;
const FireworksWordsList = ['[praise]', '[reward]'];

const tryExtractAnswer = (result: string) => {
  try {
    const obj = JSON.parse(result);

    if (obj?.answer) {
      return obj.answer;
    }

    return result;
  } catch {
    return result;
  }
};

function createMessage({
  id,
  replyToId,
  content = '',
  mine = true,
  senderDisplayName = 'Me',
  isTheChosenCandidate,
  isCandidate,
  deploymentName,
  annotation,
}: Partial<ChatMessage> & { id: string }): ChatMessage {
  let msg: ChatMessage = {
    messageType: 'chat',
    senderId: 'user',
    senderDisplayName,
    messageId: id,
    content,
    createdOn: new Date(),
    mine,
    attached: false,
    status: 'seen' as MessageStatus,
    contentType: 'richtext/html',
    replyToId,
  };

  if (isCandidate != null) {
    msg = {
      ...msg,
      isCandidate,
      isTheChosenCandidate,
      deploymentName,
    };
  }

  if (annotation) {
    msg = {
      ...msg,
      annotation,
    };
  }

  return msg;
}

const history: Message[] = [];

export const useChatManager = () => {
  const {
    temperature,
    maxTokens,
    chatPrompt,
    promotePrompt,
    evalPrompt,
    prompt,
    prompts,
    model,
    deployment,
    promptingMethod,
    ignoreDefaultFewShotExamples,
    stopSequences,
    conversationId,
    autoSave,
    update,
  } = useLLMSetting();
  const { accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const [messages, setMessages] = useState(history);
  const [shownMessage, setShownMessage] = useState(history);
  const {
    getTextCompletion,
    getChatCompletion,
    summarizeConversation,
    initializeSummarizeConversation,
  } = useTextCompletion();
  const { getAds } = useBingSerp();
  const logger = useLogger();
  const isMultiDeploymentExperiment = deployment.split(',').length > 1;
  const [isPendingUserFeedback, setIsPendingUserFeedback] = useState(false);
  const [isShowDeploymentName, setIsShowDeploymentName] = useState(false);
  const [isTriggerFireworks, setIsTriggerFireworks] = useState(false);

  const isVivara = useIsVivara();

  useEffect(() => {
    const welcomeMessage: CustomMessage = {
      messageType: 'custom',
      createdOn: new Date(),
      messageId: String(++messageId),
      content: 'welcome',
    };

    if (account) {
      const accountDisplayName = account && account.name ? account.name.split(' ')[0] : null;
      const greetingText = accountDisplayName ? `How are you doing, ${accountDisplayName}?` : 'How are you doing?';
      const greetingMessage: ChatMessage = {
        messageType: 'chat',
        senderId: 'user',
        senderDisplayName: botName,
        messageId: String(0),
        content: greetingText,
        createdOn: new Date(),
        mine: false,
        attached: false,
        status: 'seen' as MessageStatus,
        contentType: 'text',
      };
      // Log the name and replace id for logging purpose
      if (accountDisplayName) {
        logger.setAppendedData('AccountDisplayName', accountDisplayName);
        logger.setAppendedData('AccountDisplayNameReplaceId', account.localAccountId || 'Unknown');
      }

      setMessages([greetingMessage]);
      setShownMessage([welcomeMessage, greetingMessage]);
    } else {
      setShownMessage([welcomeMessage]);
    }
  }, [account, logger, isVivara]);

  const sendMessageInternal = useCallback(async (messageParam: {
    role: ChatCompletionRequestMessageRoleEnum;
    content: string;
  // eslint-disable-next-line @typescript-eslint/no-shadow
  }[], deployment: string, usrMsgId: number) => {
    logger.logVerbose(`Chat History: ${JSON.stringify(messageParam)}`);
    let result: string | undefined;

    messageParam.forEach((msg) => {
      // eslint-disable-next-line no-param-reassign
      msg.content = msg.content.replace(MARKDOWN_PREFIX, '');
    });

    if (model === LLMModel.Davinci003 && promptingMethod === PromptingMethod.Default) {
      const composedPrompt = `${chatPrompt}
        ${messageParam
    .map((msg) => `${msg.role === 'assistant' ? '¬AI¬' : '¬Human¬'}\n${msg.content}`).reduce((prev, curr) => `${prev}\n${curr}`)}
        ¬AI¬`;
      result = await getTextCompletion(composedPrompt, maxTokens, temperature);
    } else if (promptingMethod === PromptingMethod.SeparatePrompts) {
      const [
        evaluation,
        promotion,
        chat,
      ] = await Promise.all([
        // Evaluate
        getChatCompletion('', [{
          Role: ChatRole.User,
          Content: evalPrompt.replace('%s', `${messageParam.map((msg) => `${msg.role === 'assistant' ? 'assistant:' : 'user:'} ${tryExtractAnswer(msg.content)}`).reduce((prev, curr) => `${prev}\n${curr}`)}`),
        }], maxTokens, 0 /* Hard coded to 0 for a more stable result */, true, model, deployment, conversationId, autoSave).then((res) => {
          try {
            const obj = JSON.parse(res || '');

            if (obj.score && obj.explanation) {
              const score = Number.parseInt(obj.score, 10);

              if (Number.isInteger(score)) {
                return {
                  score: obj.score,
                  explanation: obj.explanation,
                };
              }
            }

            throw new Error();
          } catch {
            logger.logWarning(`Failed to parse evaluation response. Original response: ${res}`);

            return {
              score: 0,
              explanation: 'Failed to parse evaluation response',
            };
          }
        }),
        // Promote
        getChatCompletion(
          '',
          [...messageParam.map((msg) => ({
            Role: msg.role === 'assistant' ? ChatRole.Assistant : ChatRole.User,
            Content: msg.content,
          } as ChatMessageDto)), {
            Role: ChatRole.User,
            Content: promotePrompt,
          }],
          maxTokens,
          temperature,
          true,
          model,
          deployment,
          conversationId,
          autoSave,
        ).then((res) => {
          try {
            const obj = JSON.parse((res || '').replaceAll(/[^\\][\\]/g, '\\\\'));

            if (obj?.answer && obj?.query) {
              return {
                anwser: obj.answer,
                query: obj.query,
              };
            }

            throw new Error();
          } catch {
            logger.logWarning(`Failed to parse promotion response. Original response: ${res}`);

            return null;
          }
        }),
        // Chat
        getChatCompletion('', [...messageParam.map((msg) => ({
          Role: msg.role === 'assistant' ? ChatRole.Assistant : ChatRole.User,
          Content: tryExtractAnswer(msg.content),
        } as ChatMessageDto)), {
          Role: ChatRole.User,
          Content: chatPrompt,
        }], maxTokens, temperature, true, model, deployment, conversationId, autoSave),
      ]);

      const shouldPromote = evaluation?.score && evaluation.score >= 7;

      result = JSON.stringify({
        mode: shouldPromote ? 'Promote' : 'Chat',
        answer: shouldPromote ? promotion?.anwser : chat,
        score: evaluation?.score,
        query: shouldPromote ? promotion?.query : '',
        evaluation: evaluation?.explanation,
      });
    } else if (promptingMethod === PromptingMethod.Default) {
      const systemMessages = prompt ? [{
        Role: ChatRole.System,
        Content: prompt,
      } as ChatMessageDto] : [];
      result = await getChatCompletion(
        '',
        systemMessages.concat(messageParam.map((msg) => ({
          Role: msg.role === 'assistant' ? ChatRole.Assistant : ChatRole.User,
          Content: msg.content,
        } as ChatMessageDto))),
        maxTokens,
        temperature,
        ignoreDefaultFewShotExamples,
        model,
        deployment,
        conversationId,
        autoSave,
        stopSequences,
      );
    } else if (promptingMethod === PromptingMethod.MultipleSystemPrompt) {
      const filteredPrompts = compact(prompts);
      // eslint-disable-next-line @typescript-eslint/no-shadow
      const systemMessages = filteredPrompts.map((prompt) => ({
        Role: ChatRole.System,
        Content: prompt,
      } as ChatMessageDto));
      result = await getChatCompletion(
        '',
        systemMessages.concat(messageParam.map((msg) => ({
          Role: msg.role === 'assistant' ? ChatRole.Assistant : ChatRole.User,
          Content: msg.content,
        } as ChatMessageDto))),
        maxTokens,
        temperature,
        ignoreDefaultFewShotExamples,
        model,
        deployment,
        conversationId,
        autoSave,
        stopSequences,
      );
    } else if (promptingMethod === PromptingMethod.UserMode) {
      const systemMessages = prompt ? [{
        Role: ChatRole.System,
        Content: prompt,
      } as ChatMessageDto] : [];
      result = await getChatCompletion(
        '',
        systemMessages.concat(messageParam.map((msg) => ({
          Role: msg.role === 'assistant' ? ChatRole.Assistant : ChatRole.User,
          Content: msg.content,
        } as ChatMessageDto))),
        maxTokens,
        temperature,
        ignoreDefaultFewShotExamples,
        model,
        deployment,
        conversationId,
        autoSave,
        stopSequences,
      );
    }

    let replyFullContent: string | undefined;
    let replyContent: string | undefined;
    let query: string | undefined;

    try {
      if (model === LLMModel.Companion) {
        const accountDisplayName = account && account.name ? account.name.split(' ')[0] : null;
        if (accountDisplayName) {
          result = result?.replace(/(\{|\])User(\}|\])/, accountDisplayName);
        }
        logger.logWarning('Display the response from Companion model directly regarless of the format of content.');
        replyFullContent = result;
        replyContent = result;
      } else {
        // find the first { in result and split it into two parts, assign result the second part
        const firstOpenBracketIndex = result?.indexOf('{');
        let resultJson = result;
        if (firstOpenBracketIndex !== -1) {
          resultJson = result?.slice(firstOpenBracketIndex);
        }

        const parsedJson = JSON.parse(resultJson ?? '') as {
          mode: string,
          answer: string,
          score: string,
          query: string,
          evaluation: string,
        };

        // LLM may return score as a number or a string, but number doesn't have replace method
        // Since <aString>.toString just returns itself, We can safely call toString on score
        replyFullContent = `Answer: ${(parsedJson.answer ?? '').replace(/[\n]+/g, '<br><br>')}
                            Mode: ${(JSON.stringify(parsedJson.mode)).replace(/[\n]+/g, '<br><br>')}
                            Score: ${(parsedJson.score).toString().replace(/[\n]+/g, '<br><br>')}
                            Query: ${(parsedJson.query).replace(/[\n]+/g, '<br><br>')}
                            Evaluation: ${(parsedJson.evaluation ?? '').replace(/[\n]+/g, '<br><br>')}`;
        replyContent = resultJson;

        if (Number.parseInt(parsedJson.score, 10) >= 7) {
          query = parsedJson.query;
        }

        logger.logInfo(`#Mode: ${JSON.stringify(parsedJson.mode)}`);
        logger.logInfo(`#Answer: ${parsedJson.answer}`);
        logger.logInfo(`#Score: ${parsedJson.score}`);
        logger.logInfo(`#Query: ${parsedJson.query}`);
        logger.logInfo(`#Evaluation: ${parsedJson.evaluation}`);
      }
    } catch (e) {
      logger.logWarning('We are expecting a JSON response with answer, query and score, but the validation failed.');
      replyFullContent = result;
      replyContent = result;
    }

    const formattedFullResContent = (replyFullContent ?? '').replace(/^(\n|<br>)+|(\n|<br>)+$/g, '').replace(/[\n]+/g, '<br><br>');
    const formattedResContent = (replyContent ?? '').replace(/^(\n|<br>)+|(\n|<br>)+$/g, '').replace(/[\n]+/g, '<br><br>');

    return {
      formattedFullResContent,
      formattedResContent,
      query,
      deployment,
      respId: String(++messageId),
      replyToId: String(usrMsgId),
    };
  }, [
    logger,
    model,
    promptingMethod,
    chatPrompt,
    getTextCompletion,
    maxTokens,
    temperature,
    getChatCompletion,
    evalPrompt,
    promotePrompt,
    prompt,
    ignoreDefaultFewShotExamples,
    stopSequences,
    prompts,
    account,
    autoSave,
  ]);

  const sendMessage = useCallback(async (content: string) => {
    // eslint-disable-next-line no-param-reassign
    content = content.replace(/\n/g, '<br>');
    const usrMsgId = ++messageId;
    const msg = createMessage({ content, id: String(usrMsgId) });
    const newMessages = messages.concat(msg);
    const newShownMessage = shownMessage.concat(msg);
    setMessages(newMessages);
    setShownMessage(newShownMessage);

    // eslint-disable-next-line @typescript-eslint/no-shadow
    const messageParam = newMessages.filter((msg) => msg.messageType === 'chat').map((msg) => ({
      role: (msg as ChatMessage).senderDisplayName === botName ? 'assistant' : 'user' as ChatCompletionRequestMessageRoleEnum,
      content: (msg as ChatMessage).content || '',
    }));
    logger.logVerbose(`Chat History: ${JSON.stringify(messageParam)}`);

    if (!isMultiDeploymentExperiment) {
      const {
        formattedFullResContent,
        formattedResContent,
        query,
        respId,
        replyToId,
      } = await sendMessageInternal(messageParam, deployment, usrMsgId);

      if (model === LLMModel.Companion) {
        if (!isTriggerFireworks && FireworksWordsList.some((word) => formattedFullResContent.toLowerCase().includes(word))) {
          setIsTriggerFireworks(true);
        }
      }

      if (query) {
        let adsMessage = [];

        // we first try to query prod ads, and if there is no prod ads, we query text ads
        let ads = await getAds(query, 'productAds');
        logger.logInfo('fetching prod ads');

        if (ads.length > 0) {
          adsMessage = [{
            messageType: 'custom',
            senderId: 'bot',
            senderDisplayName: botName,
            createdOn: new Date(),
            mine: false,
            messageId: String(++messageId),
            content: JSON.stringify({
              type: 'ProdAds',
              ads,
              response: formattedFullResContent,
            }),
          } as CustomMessage];
        } else {
          ads = await getAds(query, 'TextAds');
          logger.logInfo('fetching text ads');

          ads = ads.slice(0, Math.min(2, ads.length));
          adsMessage = ads.length > 0
            ? [
              {
                messageType: 'custom',
                senderId: 'bot',
                senderDisplayName: botName,
                createdOn: new Date(),
                mine: false,
                messageId: String(++messageId),
                content: JSON.stringify({
                  type: 'TextAds',
                  ads,
                  response: formattedFullResContent,
                }),
              } as CustomMessage,
            ]
            : [
              {
                messageType: 'custom',
                senderId: 'bot',
                senderDisplayName: botName,
                createdOn: new Date(),
                mine: false,
                messageId: String(++messageId),
                content: JSON.stringify({
                  type: 'TextAds',
                  ads: [{
                    title: 'Ad title',
                    description: 'Ad description',
                    displayUrl: 'https://www.microsoft.com',
                    url: 'https://www.microsoft.com',
                  }],
                  response: formattedFullResContent,
                }),
              } as CustomMessage,
            ];
        }

        // show ads message along with response message
        setShownMessage(newShownMessage.concat([
          ...adsMessage,
        ]));
      } else {
        setShownMessage(newShownMessage.concat([
          createMessage({
            content: formattedFullResContent, mine: false, senderDisplayName: botName, id: respId, replyToId,
          }),
        ]));
      }

      setMessages(newMessages.concat([
        createMessage({
          content: formattedResContent, mine: false, senderDisplayName: botName, id: respId, replyToId,
        }),
      ]));
    } else {
      // eslint-disable-next-line @typescript-eslint/no-shadow
      const messageParamForMultiDeploymentExperiment = newMessages.filter((msg) => {
        if ((msg as ChatMessage).isCandidate) {
          return (msg as ChatMessage).isTheChosenCandidate;
        }
        return true;
      // eslint-disable-next-line @typescript-eslint/no-shadow
      }).map((msg) => ({
        role: (msg as ChatMessage).senderDisplayName === botName ? 'assistant' : 'user' as ChatCompletionRequestMessageRoleEnum,
        content: ((msg as ChatMessage).content?.replace(`${(msg as ChatMessage).deploymentName}:`, '') || '').trim(),
      }));
      const deployments = deployment.split(',').map((d) => d.trim());
      // eslint-disable-next-line max-len
      const requests: ReturnType<typeof sendMessageInternal>[] = deployments.map((dep) => sendMessageInternal(messageParamForMultiDeploymentExperiment, dep, usrMsgId));
      const responses = _.shuffle(await Promise.all(requests));

      const showMessagesWithResponses = responses.map((resp) => createMessage({
        content: `${resp.formattedFullResContent}`,
        mine: false,
        senderDisplayName: botName,
        isTheChosenCandidate: false,
        senderId: 'bot',
        isCandidate: true,
        id: resp.respId,
        replyToId: resp.replyToId,
        deploymentName: resp.deployment,
      }));
      setShownMessage(newShownMessage.concat(showMessagesWithResponses));
      const messageWithResponses = responses.map((resp) => createMessage({
        content: `${resp.formattedResContent}`,
        mine: false,
        senderDisplayName: botName,
        isTheChosenCandidate: false,
        senderId: 'bot',
        isCandidate: true,
        id: resp.respId,
        replyToId: resp.replyToId,
        deploymentName: resp.deployment,
      }));
      setMessages(newMessages.concat(messageWithResponses));
      setIsPendingUserFeedback(true);
      if (model === LLMModel.Companion) {
        responses.forEach((resp) => {
          if (!isTriggerFireworks && FireworksWordsList.some((word) => resp.formattedFullResContent.toLowerCase().includes(word))) {
            setIsTriggerFireworks(true);
          }
        });
      }
    }
  }, [messages, shownMessage, logger, getAds, sendMessageInternal, isMultiDeploymentExperiment, deployment, isTriggerFireworks, model]);

  const logConversation = useCallback(() => {
    const promptsToLog: object[] = [];

    if (promptingMethod === PromptingMethod.MultipleSystemPrompt) {
      promptsToLog.push(prompts.map((p, index) => ({
        prompt: p,
        name: `prompt${index + 1}`,
      })));
    } else if (promptingMethod === PromptingMethod.SeparatePrompts) {
      promptsToLog.push([{
        prompt: evalPrompt,
        name: 'scorer',
      }, {
        prompt: chatPrompt,
        name: 'chatMode',
      }, {
        prompt: promotePrompt,
        name: 'promoteMode',
      }]);
    } else {
      promptsToLog.push([{
        prompt,
        name: 'prompt',
      }]);
    }

    logger.logInfo(`[Full Conversation] ${JSON.stringify({
      conversationId,
      temperature,
      maxTokens,
      ignoreDefaultFewShotExamples,
      model,
      prompts: promptsToLog,
      chatHistory: (shownMessage as ChatMessage[]).map((m, index) => ({
        index,
        role: m.senderDisplayName,
        value: m.content,
      })),
    })}`);
  }, [
    logger,
    chatPrompt,
    conversationId,
    evalPrompt,
    ignoreDefaultFewShotExamples,
    maxTokens,
    model,
    prompt,
    promptingMethod,
    prompts,
    promotePrompt,
    shownMessage,
    temperature,
  ]);

  const restartConversation = useCallback(() => {
    logConversation();
    setMessages(history);
    setShownMessage(history);
    setIsPendingUserFeedback(false);
    update({
      prompt,
      prompts,
      chatPrompt,
      promotePrompt,
      evalPrompt,
      maxTokens,
      temperature,
      model,
      ignoreDefaultFewShotExamples,
      conversationId: uuid(),
      autoSave: true,
    });
  }, [
    setIsPendingUserFeedback,
    setMessages,
    setShownMessage,
    logConversation,
    update,
    prompt,
    prompts,
    chatPrompt,
    promotePrompt,
    evalPrompt,
    maxTokens,
    temperature,
    model,
    ignoreDefaultFewShotExamples,
  ]);

  const resetTopics = useCallback(() => {
    logger.logInfo('BackTopic button clicked');
    logConversation();
    setMessages(history);
    setShownMessage(history);
    setIsPendingUserFeedback(false);
    update({
      prompt,
      prompts,
      chatPrompt,
      promotePrompt,
      evalPrompt,
      maxTokens,
      temperature,
      model,
      ignoreDefaultFewShotExamples,
      conversationId: uuid(),
      selectedTopic: Topic.None,
      autoSave: true,
    });
  }, [
    setIsPendingUserFeedback,
    setMessages,
    setShownMessage,
    logConversation,
    update,
    prompt,
    prompts,
    chatPrompt,
    promotePrompt,
    evalPrompt,
    maxTokens,
    temperature,
    model,
    ignoreDefaultFewShotExamples,
  ]);

  useEffect(() => {
    const callback = () => {
      if (document.visibilityState === 'hidden') {
        logConversation();
      }
    };
    // Visibility Change event is more stable, but we may get duplicate partial logs
    // Unload event is less stable
    window.addEventListener('unload', callback);

    return () => window.removeEventListener('unload', callback);
  }, [logConversation]);

  useEffect(() => {
    initializeSummarizeConversation(conversationId);
  }, [conversationId, initializeSummarizeConversation]);

  useEffect(() => {
    summarizeConversation(conversationId);
  }, [conversationId]);

  const toggleBestResponse = useCallback((message: ChatMessage, isLike: boolean) => {
    const messagesWithUserFeedback = messages.map((msg) => {
      let newMsg = msg;
      if (msg.messageId === message.messageId) {
        newMsg = {
          ...newMsg,
          ...isMultiDeploymentExperiment ? { isTheChosenCandidate: isLike } : null,
          rate: isLike ? 'like' : '',
        } as ChatMessage;
      }
      if (_.result(msg, 'replyToId') === message.replyToId) {
        newMsg = {
          ...newMsg,
          ...isMultiDeploymentExperiment ? { isCandidateSelectEnd: true } : null,
        } as ChatMessage;
      }
      return newMsg;
    });
    setMessages(messagesWithUserFeedback);

    if (isMultiDeploymentExperiment) {
      setIsPendingUserFeedback(false);
    }
  }, [messages, isMultiDeploymentExperiment]);

  // like or dislike
  const collectRate = useCallback((message: ChatMessage, rate: string) => {
    const messagesWithUserFeedback = messages.map((msg) => {
      if (msg.messageId === message.messageId) {
        return {
          ...msg,
          rate,
        } as ChatMessage;
      }
      return msg;
    });

    setMessages(messagesWithUserFeedback);
  }, [messages]);

  const collectAnnotations = useCallback((message: ChatMessage, annotation: string) => {
    const messagesWithUserFeedback = messages.map((msg) => {
      if (msg.messageId === message.messageId) {
        return {
          ...msg,
          annotation,
        } as ChatMessage;
      }
      return msg;
    });

    setMessages(messagesWithUserFeedback);
  }, [messages]);

  const getConversaionHistory = useCallback(() => {
    const result: IMultiDeploymentChatHistory = {
      conversation: [],
    };

    const startIndex = messages.findIndex((msg) => (msg as ChatMessage).mine);
    let i = startIndex;
    while (i < messages.length) {
      const msg = messages[i] as ChatMessage;
      const convItem: IMultiDeploymentChatHistoryItem = {
        user: msg?.content || '',
        chatbots: [],
      };
      result.conversation.push(convItem);

      let j = i + 1;
      if (msg?.mine) {
        while (j < messages.length) {
          const botMsg = messages[j] as ChatMessage;
          if (!botMsg.mine) {
            convItem.chatbots.push({
              name: isMultiDeploymentExperiment ? botMsg.deploymentName : deployment,
              response: botMsg.content,
              selected: !!botMsg.isTheChosenCandidate,
              rate: botMsg.rate,
              annotation: botMsg.annotation,
            });
            // sort chatbot by their names
            convItem.chatbots = convItem.chatbots.sort((a, b) => {
              if (a.name == null) {
                return -1;
              }

              if (b.name == null) {
                return 1;
              }

              return a.name.localeCompare(b.name);
            });
            j++;
          } else {
            i = j;
            break;
          }
        }
      }
      i = j;
    }
    return result;
  }, [messages, deployment, isMultiDeploymentExperiment]);

  const saveConversationHistory = useCallback(() => {
    const result: IMultiDeploymentChatHistory = getConversaionHistory();
    // eslint-disable-next-line no-console
    console.log(isMultiDeploymentExperiment ? '[Multi-Deployment conversation history]' : '[Conversation history]', {
      conversationId,
      history: result,
    });
    logger.logInfo(`${isMultiDeploymentExperiment ? '[Multi-Deployment conversation history]' : '[Conversation history]'}, ${JSON.stringify({
      conversationId,
      history: result,
    })}`);
  }, [logger, conversationId, getConversaionHistory, isMultiDeploymentExperiment]);

  return {
    isPendingUserFeedback,
    isMultiDeploymentExperiment,
    shownMessage,
    sendMessage,
    restartConversation,
    resetTopics,
    messages,
    toggleBestResponse,
    getConversaionHistory,
    saveConversationHistory,
    isShowDeploymentName,
    setIsShowDeploymentName,
    isTriggerFireworks,
    setIsTriggerFireworks,
    collectRate,
    collectAnnotations,
  };
};
